import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const wraps = [
  { src: "/assets/vgraphics/wrap-hod-piano.jpg", label: "House of Dez · The Newbury Boston" },
  { src: "/assets/vgraphics/wrap-hod-dj.jpg", label: "House of Dez · Custom DJ Booth" },
  { src: "/assets/vgraphics/wrap-white-sofa.webp", label: "All-White Sweetheart Stage" },
  { src: "/assets/vgraphics/wrap-gold-lines.webp", label: "Gold Mirror Backdrop Stage" },
];

export default function StageWraps() {
  const [revealed, setRevealed] = useState(false);

  return (
    <section id="stage-wraps" data-testid="stage-wraps-section" className="relative bg-ivory py-32 sm:py-44 overflow-hidden">
      <div className="absolute -bottom-24 right-0 w-[460px] h-[460px] rounded-full bg-blush/12 blur-[120px]" aria-hidden="true" />
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 relative">
        <FadeUp className="mb-6">
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral">Custom Stage Wraps</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-8">
          <MaskedLines inView lines={[<>An empty stage becomes <span className="italic text-coral">the moment</span></>]} />
        </h2>
        <FadeUp delay={0.1} className="mb-12 sm:mb-14">
          <p className="text-ink/60 text-base max-w-xl">
            We dress and wrap your stage from the ground up. Tap reveal and watch a bare
            platform transform into the perfect stage for the bride and groom.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div
            data-testid="stage-reveal-frame"
            className="relative w-full max-w-[680px] mx-auto aspect-[3/4] rounded-[2rem] overflow-hidden shadow-[0_40px_90px_-30px_rgba(45,40,51,0.45)]"
          >
            <img
              data-testid="stage-before-image"
              src="/assets/vgraphics/stage-before.webp"
              alt="Before — a bare black event stage in the ballroom"
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
            />
            <motion.div
              className="absolute inset-0"
              initial={false}
              animate={{ clipPath: revealed ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)" }}
              transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
            >
              <img
                data-testid="stage-after-image"
                src="/assets/vgraphics/stage-after.webp"
                alt="After — a custom white-wrapped stage with floral backdrop and drapery"
                className="absolute inset-0 w-full h-full object-cover"
                draggable="false"
              />
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.span
                key={revealed ? "after" : "before"}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.4 }}
                className={`absolute top-5 left-6 px-4 py-1.5 rounded-full backdrop-blur-md text-[11px] tracking-[0.35em] uppercase pointer-events-none ${
                  revealed ? "bg-ivory/70 text-ink" : "bg-midnight/50 text-ivory"
                }`}
              >
                {revealed ? "After" : "Before"}
              </motion.span>
            </AnimatePresence>

            <div className="absolute left-1/2 bottom-6 -translate-x-1/2 z-10">
              <motion.button
                data-testid="stage-reveal-button"
                onClick={() => setRevealed((v) => !v)}
                className="inline-flex items-center gap-3 rounded-full bg-ink/85 backdrop-blur-md text-ivory text-[13px] tracking-[0.2em] uppercase px-9 py-4 hover:bg-coral transition-colors duration-300 shadow-[0_18px_40px_-14px_rgba(0,0,0,0.6)] whitespace-nowrap"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                {revealed ? <RotateCcw size={16} strokeWidth={1.5} /> : <Sparkles size={16} strokeWidth={1.5} />}
                {revealed ? "Show the empty stage" : "Reveal the magic"}
              </motion.button>
            </div>
          </div>
        </FadeUp>

        <div data-testid="stage-wrap-gallery" className="mt-20 sm:mt-28 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {wraps.map((w, i) => (
            <FadeUp key={w.src} delay={i * 0.08} data-testid={`stage-wrap-photo-${i}`} className="group relative overflow-hidden rounded-[1.5rem] spotlight-frame aspect-[4/5]">
              <motion.img
                src={w.src}
                alt={w.label}
                loading="lazy"
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.06 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight/55 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 right-3 text-ivory text-[11px] tracking-[0.25em] uppercase">
                {w.label}
              </p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
